import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { GtmService } from './gtm/gtm.service';

export interface RouteTrackingData {
  page_path: string;
  page_title: string;
  page_location: string;
  previous_page?: string;
  navigation_type?: 'direct' | 'navigation';
  timestamp: string;
  user_agent?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RouteTrackingService {
  private previousRoute: string = '';
  private currentRoute: string = '';

  constructor(private router: Router, private gtmService: GtmService) {
    this.initializeRouteTracking();
  }

  /**
   * Inicializa el tracking de rutas
   */
  private initializeRouteTracking(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.trackRouteChange(event);
    });
  }

  /**
   * Trackea el cambio de ruta
   */
  private trackRouteChange(event: NavigationEnd): void {
    const currentUrl = event.urlAfterRedirects;
    const previousUrl = this.previousRoute;

    // Actualizar rutas
    this.previousRoute = this.currentRoute;
    this.currentRoute = currentUrl;

    // Obtener el título de la página
    const pageTitle = this.getPageTitle(currentUrl);

    // Preparar datos para GTM
    const trackingData: RouteTrackingData = {
      page_path: currentUrl,
      page_title: pageTitle,
      page_location: window.location.origin + currentUrl,
      previous_page: previousUrl || 'direct_access',
      navigation_type: previousUrl ? 'navigation' : 'direct',
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
    };

    // Enviar evento a GTM
    this.gtmService.sendEvent('page_view', trackingData);

    // También enviar un evento específico de navegación
    this.gtmService.sendEvent('route_change', {
      ...trackingData,
      route_name: this.getRouteName(currentUrl),
      route_category: this.getRouteCategory(currentUrl),
    });

    console.log('🔄 Route tracking:', trackingData);
  }

  /**
   * Obtiene el título de la página basado en la ruta
   */
  private getPageTitle(url: string): string {
    const routeMap: { [key: string]: string } = {
      '/portal': 'Portal de Promociones',
      '/historical': 'Historial de Transacciones',
      '/webpay': 'Página de Pago WebPay',
      '/oneclick': 'Página de Pago OneClick',
      '/mercadopago': 'Página de Pago MercadoPago',
      '/payment-page': 'Página de Pago',
      '/payment-complete': 'Pago Completado',
      '/refund': 'Página de Reembolso',
      '/my-happy-plus': 'Mi Perfil Happy Plus',
      '/waiver': 'Página de Exención',
      '/card-error': 'Error de Tarjeta',
      '/server-error': 'Error del Servidor',
    };

    // Buscar coincidencia exacta primero
    if (routeMap[url]) {
      return routeMap[url];
    }

    // Buscar coincidencia parcial para rutas con parámetros
    for (const [route, title] of Object.entries(routeMap)) {
      if (url.startsWith(route)) {
        return title;
      }
    }

    return 'Página Desconocida';
  }

  /**
   * Obtiene el nombre de la ruta sin parámetros
   */
  private getRouteName(url: string): string {
    const cleanUrl = url.split('?')[0]; // Remover query parameters
    const segments = cleanUrl.split('/').filter((segment) => segment.length > 0);
    return segments[0] || 'home';
  }

  /**
   * Obtiene la categoría de la ruta
   */
  private getRouteCategory(url: string): string {
    const paymentRoutes = ['/webpay', '/oneclick', '/mercadopago', '/payment-page', '/payment-complete'];
    const errorRoutes = ['/card-error', '/server-error'];
    const profileRoutes = ['/my-happy-plus', '/historical'];
    const portalRoutes = ['/portal', '/waiver'];

    if (paymentRoutes.some((route) => url.startsWith(route))) {
      return 'payment';
    }
    if (errorRoutes.some((route) => url.startsWith(route))) {
      return 'error';
    }
    if (profileRoutes.some((route) => url.startsWith(route))) {
      return 'profile';
    }
    if (portalRoutes.some((route) => url.startsWith(route))) {
      return 'portal';
    }

    return 'other';
  }

  /**
   * Trackea manualmente una navegación específica
   */
  trackManualNavigation(route: string, additionalData?: any): void {
    const trackingData: RouteTrackingData = {
      page_path: route,
      page_title: this.getPageTitle(route),
      page_location: window.location.origin + route,
      previous_page: this.currentRoute || 'manual_tracking',
      navigation_type: 'manual',
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      ...additionalData,
    };

    this.gtmService.sendEvent('manual_route_tracking', trackingData);
    console.log('📝 Manual route tracking:', trackingData);
  }

  /**
   * Obtiene estadísticas de navegación
   */
  getNavigationStats(): any {
    return {
      current_route: this.currentRoute,
      previous_route: this.previousRoute,
      total_navigations: this.currentRoute ? 1 : 0, // Simplificado, en una implementación real se contaría
      timestamp: new Date().toISOString(),
    };
  }
}
